import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Truck, MapPin, Gift, Info, Loader2 } from 'lucide-react';

const DeliveryCostCalculator = ({ stationId, cartTotal, onCostCalculated }) => {
  const { api, user } = useAuth();
  const [deliveryCost, setDeliveryCost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [zones, setZones] = useState([]);
  const [showZonesInfo, setShowZonesInfo] = useState(false);

  // Fetch delivery zones on mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await api().get('/delivery/zones');
        setZones(res.data.zones);
      } catch (err) {
        console.error('Error fetching zones:', err);
      }
    };
    fetchZones();
  }, [api]);

  // Calculate delivery cost
  const calculateCost = useCallback(async () => {
    if (!stationId) return;
    
    setLoading(true);
    try {
      const payload = {
        station_id: stationId,
        ...(userLocation && {
          latitude: userLocation.lat,
          longitude: userLocation.lon
        })
      };
      
      const res = await api().post('/delivery/calculate-cost', payload);
      setDeliveryCost(res.data);
      if (onCostCalculated) {
        onCostCalculated(res.data);
      }
    } catch (err) {
      console.error('Error calculating delivery cost:', err);
    }
    setLoading(false);
  }, [api, stationId, userLocation, onCostCalculated]);

  // Recalculate when station changes
  useEffect(() => {
    calculateCost();
  }, [calculateCost]);

  // Get user location
  const getLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Try to get location on mount
  useEffect(() => {
    getLocation();
  }, []);

  const getZoneLabel = (zone) => {
    const labels = {
      zone_1: { name: 'Zona 1', desc: '0-5 km', color: 'bg-green-100 text-green-700' },
      zone_2: { name: 'Zona 2', desc: '5-10 km', color: 'bg-yellow-100 text-yellow-700' },
      zone_3: { name: 'Zona 3', desc: '+10 km', color: 'bg-orange-100 text-orange-700' }
    };
    return labels[zone] || { name: zone, desc: '', color: 'bg-zinc-100 text-zinc-700' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-zinc-200 p-4">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-sonangol-red" />
          <span className="text-sm text-zinc-500">A calcular custo de entrega...</span>
        </div>
      </div>
    );
  }

  if (!deliveryCost) return null;

  const zoneInfo = getZoneLabel(deliveryCost.zone);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-sonangol-red" />
          <span className="font-barlow font-bold text-zinc-800">Custo de Entrega</span>
        </div>
        <button
          onClick={() => setShowZonesInfo(!showZonesInfo)}
          className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-zinc-200"
          data-testid="zones-info-btn"
        >
          <Info className="w-4 h-4 text-zinc-500" />
        </button>
      </div>

      {/* Zones Info Panel */}
      {showZonesInfo && (
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-xs text-blue-700 font-medium mb-2">Tabela de Preços por Zona:</p>
          <div className="space-y-1">
            {zones.map((zone) => (
              <div key={zone.zone} className="flex justify-between text-xs">
                <span className="text-blue-600">{zone.description}</span>
                <span className="font-semibold text-blue-800">{zone.cost.toLocaleString('pt-AO')} AOA</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2 pt-2 border-t border-blue-200">
            Entrega grátis para compras acima de 50.000 AOA
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 space-y-3">
        {/* Distance & Zone */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-zinc-400" />
            <span className="text-sm text-zinc-600">
              Distância estimada: <span className="font-semibold">{deliveryCost.distance_km} km</span>
            </span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${zoneInfo.color}`}>
            {zoneInfo.name}
          </span>
        </div>

        {/* Cost Display */}
        {deliveryCost.is_free ? (
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-100">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Gift className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-barlow font-bold text-green-700">Entrega Grátis!</p>
              <p className="text-xs text-green-600">{deliveryCost.free_delivery_reason}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl">
            <div>
              <p className="text-xs text-zinc-500">Taxa de entrega ({zoneInfo.desc})</p>
              <p className="font-barlow font-bold text-lg text-zinc-800">
                {deliveryCost.delivery_cost.toLocaleString('pt-AO')} AOA
              </p>
            </div>
            {cartTotal < 50000 && (
              <div className="text-right">
                <p className="text-xs text-zinc-400">Faltam</p>
                <p className="text-sm font-semibold text-sonangol-red">
                  {(50000 - cartTotal).toLocaleString('pt-AO')} AOA
                </p>
                <p className="text-xs text-zinc-400">para entrega grátis</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryCostCalculator;
